import {
  AddCustomer,
  PostCustomer2FA,
  GetCustomers,
  PutCustomer,
  GetCustomerSubscription,
  GetCustomerSubscriptions,
  PostCustomerSubscription,
  PutCustomerSubscription,
  GetCustomer,
  DeleteCustomerSubscription,
  GetCustomerData,
  PostMergeCustomers,
  GetCustomerSubscriptionInvoice,
  PutCustomerSubscriptionInvoice,
  GetCustomerLabels,
  PostCustomerLabel,
  PostCustomerSubscriptionVisitEvent,
} from "@/server-types";
import { post, get, put, del } from "../request-helpers";
import { useCustomMutation } from "../custom-mutation";
import { useCustomQuery } from "../custom-query";
import { useInfiniteQuery } from "@tanstack/react-query";
import { queryClient } from "..";

export const useGetCustomer = (customerId: number) => {
  return useCustomQuery({
    queryKey: ["customer", customerId],
    queryFn: () =>
      get<GetCustomerData>(`/appointment/getCustomer`, {
        customerId,
      }),
  });
};

export const useGetCustomerNew = (customerId: number) => {
  return useCustomQuery({
    queryKey: ["customer", customerId],
    queryFn: () => get<GetCustomer>(`/customer/${customerId}`),
    enabled: customerId !== -1,
  });
};

export const usePostMergeCustomers = () => {
  return useCustomMutation({
    mutationFn: (body: PostMergeCustomers["body"]) =>
      post<PostMergeCustomers>("/customer/merge", body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getCustomerList"] });
    },
  });
};

/**
 * POST /customers
 * /
 */

export const useAddCustomer = () => {
  return useCustomMutation({
    mutationFn: (body: AddCustomer["query"]) =>
      post<AddCustomer>("/customer", body),
  });
};

export const useConfirmCustomer2FA = () => {
  return useCustomMutation({
    mutationFn: ({
      customerId,
      body,
    }: {
      customerId: number;
      body: PostCustomer2FA["body"];
    }) => post<PostCustomer2FA>(`/customer/${customerId}/2fa`, body),
  });
};

export const useGetCustomers = (
  query: GetCustomers["query"] & {
    enabled?: boolean;
  },
) => {
  return useCustomQuery({
    queryKey: ["customers", query],
    queryFn: () => get<GetCustomers>("/customer", query),
    enabled: query.enabled ?? true,
  });
};

export const useInfiniteGetCustomers = (
  query: Omit<GetCustomers["query"], "locationId"> & {
    locationId?: number;
    businessPremiseId?: string;
  },
) => {
  return useInfiniteQuery({
    queryKey: ["getCustomersInfinite", query],
    queryFn: async ({ pageParam = 0 }) => {
      return get<GetCustomers>("/customer", {
        ...query,
        pagination: {
          ...query.pagination,
          offset: pageParam as number,
        },
      });
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.nextOffset,
  });
};

export const useGetCustomersMutation = () => {
  return useCustomMutation({
    mutationFn: (body: GetCustomers["query"]) =>
      get<GetCustomers>("/customer", body),
  });
};

export const usePutCustomer = () => {
  return useCustomMutation({
    mutationFn: ({
      customerId,
      body,
    }: {
      customerId: number;
      body: PutCustomer["body"];
    }) => put<PutCustomer>(`/customer/${customerId}`, body),
  });
};

export const useGetCustomerSubscriptions = (
  query: GetCustomerSubscriptions["query"],
) => {
  return useCustomQuery({
    enabled: query.customerId !== -1,
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: ["customer-subscription", query.customerId],
    queryFn: () =>
      get<GetCustomerSubscriptions>("/customer-subscription", query),
  });
};

export const useGetCustomerSubscription = ({
  customerSubscriptionId,
}: {
  customerSubscriptionId: number;
}) => {
  return useCustomQuery({
    queryKey: ["customer-subscription", customerSubscriptionId],
    queryFn: () =>
      get<GetCustomerSubscription>(
        `/customer-subscription/${customerSubscriptionId}`,
      ),
  });
};

export const useCreateCustomerSubscription = () => {
  return useCustomMutation({
    mutationFn: ({ body }: { body: PostCustomerSubscription["body"] }) =>
      post<PostCustomerSubscription>("/customer-subscription/", body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customer-subscription"] });
    },
  });
};

export const useUpdateCustomerSubscription = () => {
  return useCustomMutation({
    mutationFn: ({
      customerSubscriptionId,
      body,
    }: {
      customerSubscriptionId: number;
      body: PutCustomerSubscription["body"];
    }) =>
      put<PutCustomerSubscription>(
        `/customer-subscription/${customerSubscriptionId}`,
        body,
      ),
  });
};

export const useCancelCustomerSubscription = () => {
  return useCustomMutation({
    mutationFn: ({
      customerSubscriptionId,
    }: {
      customerSubscriptionId: number;
    }) =>
      del<DeleteCustomerSubscription>(
        `/customer-subscription/${customerSubscriptionId}`,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customer-subscription"] });
    },
  });
};

export const useGetSubscriptionInvoices = ({
  customerSubscriptionId,
}: {
  customerSubscriptionId: number;
}) => {
  return useCustomQuery({
    queryKey: ["subscription-invoices", customerSubscriptionId],
    queryFn: () =>
      get<GetCustomerSubscriptionInvoice>(
        `/customer-subscription/${customerSubscriptionId}/invoices`,
      ),
  });
};

export const useUpdateSubscriptionInvoice = () => {
  return useCustomMutation({
    mutationFn: ({
      invoiceId,
      body,
    }: {
      invoiceId: number;
      body: PutCustomerSubscriptionInvoice["body"];
    }) =>
      put<PutCustomerSubscriptionInvoice>(
        `/customer-subscription/invoices/${invoiceId}`,
        body,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subscription-invoices"] });
    },
  });
};

export const useGetCustomerLabels = () => {
  return useCustomQuery({
    queryKey: ["customer-labels"],
    queryFn: () => get<GetCustomerLabels>("/customer/label"),
  });
};

export const usePostCustomerLabel = () => {
  return useCustomMutation({
    mutationFn: (body: PostCustomerLabel["body"]) =>
      post<PostCustomerLabel>("/customer/label", body),
  });
};

export const usePostCustomerSubscriptionVisitEvent = () => {
  return useCustomMutation({
    mutationFn: ({
      customerSubscriptionId,
      body,
    }: {
      customerSubscriptionId: number;
      body: PostCustomerSubscriptionVisitEvent["body"];
    }) =>
      post<PostCustomerSubscriptionVisitEvent>(
        `/customer-subscription/${customerSubscriptionId}/visit`,
        body,
      ),
  });
};
